import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import { REALTIME } from '@/config/constants';
import { logout } from '@/features/auth/stores/authSlice';

import type {
  RealtimeState,
  IoTDataPoint,
  Channel,
  ConnectionStatus,
  StationTelemetryOverlayEntry,
  IesRealtimeAlert,
} from '../types/realtime.types';
import type { IotTelemetryMergePayload } from '../utils/iotTelemetryPayload';
import { PRIMARY_ROUTING_ORDER } from '../utils/stationSensorBindings';

/** Giữ ngắn — map chỉ cần các lưu vực có alert gần nhất. */
const IES_REALTIME_ALERTS_CAP = 24;

const initialState: RealtimeState = {
  status: 'disconnected',
  channels: [],
  dataBuffer: [],
  lastUpdate: null,
  telemetryByStationId: {},
  iesRealtimeAlerts: [],
};

const realtimeSlice = createSlice({
  name: 'realtime',
  initialState,
  reducers: {
    connected(state) {
      state.status = 'connected';
    },
    disconnected(state) {
      state.status = 'disconnected';
    },
    setStatus(state, action: PayloadAction<ConnectionStatus>) {
      state.status = action.payload;
    },
    addChannel(state, action: PayloadAction<Channel>) {
      if (!state.channels.find((c) => c.id === action.payload.id)) {
        state.channels.push(action.payload);
      }
    },
    removeChannel(state, action: PayloadAction<string>) {
      state.channels = state.channels.filter((c) => c.id !== action.payload);
    },
    dataReceived(state, action: PayloadAction<IoTDataPoint>) {
      state.dataBuffer.push(action.payload);
      if (state.dataBuffer.length > REALTIME.DATA_BUFFER_SIZE) {
        state.dataBuffer = state.dataBuffer.slice(-REALTIME.DATA_BUFFER_SIZE);
      }
      state.lastUpdate = Date.now();
    },
    batchDataReceived(state, action: PayloadAction<IoTDataPoint[]>) {
      state.dataBuffer.push(...action.payload);
      if (state.dataBuffer.length > REALTIME.DATA_BUFFER_SIZE) {
        state.dataBuffer = state.dataBuffer.slice(-REALTIME.DATA_BUFFER_SIZE);
      }
      state.lastUpdate = Date.now();
    },
    clearBuffer(state) {
      state.dataBuffer = [];
    },
    batchTelemetryStationUpdate(state, action: PayloadAction<IotTelemetryMergePayload[]>) {
      const now = Date.now();
      for (const p of action.payload) {
        for (const k of p.keys) {
          const prev = state.telemetryByStationId[k];
          const readingsByChannelId = { ...(prev?.readingsByChannelId ?? {}) };
          const readingsByRoutingKey = { ...(prev?.readingsByRoutingKey ?? {}) };
          if (p.channelId) readingsByChannelId[p.channelId] = p.value;
          if (p.routingKey) readingsByRoutingKey[p.routingKey] = p.value;
          let primaryValue = p.value;
          for (const rk of PRIMARY_ROUTING_ORDER) {
            if (readingsByRoutingKey[rk] != null) {
              primaryValue = readingsByRoutingKey[rk]!;
              break;
            }
          }
          const entry: StationTelemetryOverlayEntry = {
            value: primaryValue,
            timestamp: p.timestamp ?? now,
            lastUpdated: now,
            readingsByChannelId:
              Object.keys(readingsByChannelId).length > 0 ? readingsByChannelId : undefined,
            readingsByRoutingKey:
              Object.keys(readingsByRoutingKey).length > 0 ? readingsByRoutingKey : undefined,
            ...(p.routingKey === 'rainfall' && p.durationH != null
              ? { lastRainfallDurationH: p.durationH }
              : {}),
          };
          state.telemetryByStationId[k] = entry;
        }
      }
      state.lastUpdate = now;
    },
    clearTelemetryOverlay(state) {
      state.telemetryByStationId = {};
    },
    iesAlertReceived(state, action: PayloadAction<IesRealtimeAlert>) {
      state.iesRealtimeAlerts = [...state.iesRealtimeAlerts, action.payload].slice(
        -IES_REALTIME_ALERTS_CAP,
      );
      state.lastUpdate = Date.now();
    },
    clearIesRealtimeAlerts(state) {
      state.iesRealtimeAlerts = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logout, (state) => {
      state.telemetryByStationId = {};
      state.dataBuffer = [];
      state.lastUpdate = null;
      state.status = 'disconnected';
      state.iesRealtimeAlerts = [];
    });
  },
});

export const {
  connected,
  disconnected,
  setStatus,
  addChannel,
  removeChannel,
  dataReceived,
  batchDataReceived,
  clearBuffer,
  batchTelemetryStationUpdate,
  clearTelemetryOverlay,
  iesAlertReceived,
  clearIesRealtimeAlerts,
} = realtimeSlice.actions;
export default realtimeSlice.reducer;
